import { apiV1 } from './apiClient';

/**
 * Servicio para gestión de campos dinámicos
 * Consume endpoints de backend/src/routes/v1/camposDinamicosRoutes.js
 */
const camposDinamicosApi = {
  // ========================================
  // CATÁLOGO DE CAMPOS
  // ========================================

  /**
   * GET /api/v1/campos-dinamicos
   * Listar campos del catálogo
   * @param {Object} [params] - Filtros opcionales
   * @param {boolean} [params.solo_globales] - Solo campos globales
   * @param {boolean} [params.solo_activos] - Solo campos activos
   * @param {string} [params.tipo_campo] - Filtrar por tipo
   * @returns {Promise<Array>} Lista de campos
   */
  getCampos: async (params = {}) => {
    const response = await apiV1.get('/campos-dinamicos', { params });
    return response.data;
  },

  /**
   * GET /api/v1/campos-dinamicos/:id
   * Obtener detalle de un campo específico
   * @param {number} id - ID del campo
   * @returns {Promise<Object>} Detalle del campo
   */
  getCampo: async (id) => {
    const response = await apiV1.get(`/campos-dinamicos/${id}`);
    return response.data;
  },

  /**
   * POST /api/v1/campos-dinamicos
   * Crear nuevo campo en el catálogo
   * @param {Object} campoData - Datos del campo
   * @param {string} campoData.nombre_campo - Nombre técnico (snake_case)
   * @param {string} campoData.etiqueta_display - Etiqueta para mostrar
   * @param {string} campoData.tipo_campo - Tipo: texto|numero|fecha|boolean|select|textarea|email|telefono|url|archivo
   * @param {string} [campoData.descripcion] - Descripción del campo
   * @param {Object} [campoData.configuracion_campo] - Config específica (opciones, placeholder, etc)
   * @param {Object} [campoData.validaciones] - Validaciones (required, regex, min, max, etc)
   * @param {boolean} [campoData.es_global] - Si es campo global (compartido)
   * @returns {Promise<Object>} Campo creado
   */
  createCampo: async (campoData) => {
    const response = await apiV1.post('/campos-dinamicos', campoData);
    return response.data;
  },

  /**
   * PUT /api/v1/campos-dinamicos/:id
   * Actualizar campo existente
   * @param {number} id - ID del campo
   * @param {Object} campoData - Datos a actualizar
   * @returns {Promise<Object>} Campo actualizado
   */
  updateCampo: async (id, campoData) => {
    const response = await apiV1.put(`/campos-dinamicos/${id}`, campoData);
    return response.data;
  },

  /**
   * DELETE /api/v1/campos-dinamicos/:id
   * Desactivar campo (soft delete)
   * @param {number} id - ID del campo
   * @returns {Promise<Object>} Resultado
   */
  deleteCampo: async (id) => {
    const response = await apiV1.delete(`/campos-dinamicos/${id}`);
    return response.data;
  },

  // ========================================
  // ASIGNACIÓN DE CAMPOS A ESTADOS
  // ========================================

  /**
   * GET /api/v1/campos-dinamicos/estado/:idEstado
   * Obtener campos asignados a un estado específico
   * @param {number} idEstado - ID del estado
   * @returns {Promise<Array>} Campos del estado con info de asignación
   */
  getCamposPorEstado: async (idEstado) => {
    const response = await apiV1.get(`/campos-dinamicos/estado/${idEstado}`);
    return response.data;
  },

  /**
   * GET /api/v1/campos-dinamicos/empresa/todos
   * Obtener todos los campos por estado de la empresa
   * @returns {Promise<Object>} Objeto con estados como keys y arrays de campos como values
   */
  getCamposPorEmpresa: async () => {
    const response = await apiV1.get('/campos-dinamicos/empresa/todos');
    return response.data;
  },

  /**
   * POST /api/v1/campos-dinamicos/asignar
   * Asignar un campo a un estado
   * @param {Object} asignacionData - Datos de asignación
   * @param {number} asignacionData.id_estado - ID del estado
   * @param {number} asignacionData.id_campo - ID del campo
   * @param {boolean} asignacionData.es_obligatorio - Si es obligatorio
   * @param {number} [asignacionData.orden_display] - Orden de visualización
   * @returns {Promise<Object>} Asignación creada
   */
  asignarCampo: async (asignacionData) => {
    const response = await apiV1.post('/campos-dinamicos/asignar', asignacionData);
    return response.data;
  },

  /**
   * PUT /api/v1/campos-dinamicos/asignacion/:id
   * Actualizar asignación de campo a estado
   * @param {number} id - ID de la asignación
   * @param {Object} asignacionData - Datos a actualizar
   * @returns {Promise<Object>} Asignación actualizada
   */
  updateAsignacion: async (id, asignacionData) => {
    const response = await apiV1.put(`/campos-dinamicos/asignacion/${id}`, asignacionData);
    return response.data;
  },

  /**
   * DELETE /api/v1/campos-dinamicos/asignacion/:id
   * Eliminar asignación de campo a estado
   * @param {number} id - ID de la asignación
   * @returns {Promise<Object>} Resultado
   */
  deleteAsignacion: async (id) => {
    const response = await apiV1.delete(`/campos-dinamicos/asignacion/${id}`);
    return response.data;
  }
};

export default camposDinamicosApi;
